export type MovementInput = {
  x: number;
  y: number;
  active: boolean;
  source: "none" | "pointer" | "keyboard" | "mixed";
};

export type InputManagerOptions = {
  target: HTMLElement | Window;
  getPlayerScreenPosition: () => { x: number; y: number };
  onChange?: (input: MovementInput) => void;
};

type KeyState = {
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
};

type PointerState = {
  id: number;
  x: number;
  y: number;
};

export class InputManager {
  private readonly target: HTMLElement | Window;
  private readonly getPlayerScreenPosition: () => { x: number; y: number };
  private readonly onChange?: (input: MovementInput) => void;
  private readonly keys: KeyState = {
    up: false,
    down: false,
    left: false,
    right: false
  };

  private readonly pointers = new Map<number, PointerState>();
  private activePointerId: number | null = null;
  private current: MovementInput = {
    x: 0,
    y: 0,
    active: false,
    source: "none"
  };

  public constructor(options: InputManagerOptions) {
    this.target = options.target;
    this.getPlayerScreenPosition = options.getPlayerScreenPosition;
    this.onChange = options.onChange;
  }

  public attach(): void {
    const pointerTarget = this.target instanceof Window ? window : this.target;

    pointerTarget.addEventListener("pointerdown", this.onPointerDown as EventListener, {
      passive: false
    });
    pointerTarget.addEventListener("pointermove", this.onPointerMove as EventListener, {
      passive: false
    });
    pointerTarget.addEventListener("pointerup", this.onPointerUp as EventListener, {
      passive: false
    });
    pointerTarget.addEventListener("pointercancel", this.onPointerUp as EventListener, {
      passive: false
    });
    pointerTarget.addEventListener("lostpointercapture", this.onPointerLost as EventListener, {
      passive: false
    });

    window.addEventListener("keydown", this.onKeyDown, { passive: false });
    window.addEventListener("keyup", this.onKeyUp, { passive: false });
    window.addEventListener("blur", this.onBlur);
  }

  public detach(): void {
    const pointerTarget = this.target instanceof Window ? window : this.target;

    pointerTarget.removeEventListener("pointerdown", this.onPointerDown as EventListener);
    pointerTarget.removeEventListener("pointermove", this.onPointerMove as EventListener);
    pointerTarget.removeEventListener("pointerup", this.onPointerUp as EventListener);
    pointerTarget.removeEventListener("pointercancel", this.onPointerUp as EventListener);
    pointerTarget.removeEventListener("lostpointercapture", this.onPointerLost as EventListener);

    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("blur", this.onBlur);
  }

  public getMovement(): MovementInput {
    return this.current;
  }

  public update(): MovementInput {
    const keyboard = this.keyboardVector();
    const pointer = this.pointerVector();

    let x = 0;
    let y = 0;
    let source: MovementInput["source"] = "none";

    if (keyboard.active && pointer.active) {
      x = keyboard.x + pointer.x;
      y = keyboard.y + pointer.y;
      source = "mixed";
    } else if (keyboard.active) {
      x = keyboard.x;
      y = keyboard.y;
      source = "keyboard";
    } else if (pointer.active) {
      x = pointer.x;
      y = pointer.y;
      source = "pointer";
    }

    const normalized = normalize(x, y);

    this.current = {
      x: normalized.x,
      y: normalized.y,
      active: source !== "none",
      source
    };

    this.onChange?.(this.current);
    return this.current;
  }

  private keyboardVector(): MovementInput {
    let x = 0;
    let y = 0;

    if (this.keys.left) {
      x -= 1;
    }

    if (this.keys.right) {
      x += 1;
    }

    if (this.keys.up) {
      y -= 1;
    }

    if (this.keys.down) {
      y += 1;
    }

    const normalized = normalize(x, y);

    return {
      x: normalized.x,
      y: normalized.y,
      active: x !== 0 || y !== 0,
      source: x !== 0 || y !== 0 ? "keyboard" : "none"
    };
  }

  private pointerVector(): MovementInput {
    const pointer = this.activePointer();

    if (!pointer) {
      return {
        x: 0,
        y: 0,
        active: false,
        source: "none"
      };
    }

    const player = this.getPlayerScreenPosition();
    const dx = pointer.x - player.x;
    const dy = pointer.y - player.y;
    const normalized = normalize(dx, dy);

    return {
      x: normalized.x,
      y: normalized.y,
      active: Math.abs(dx) > 8 || Math.abs(dy) > 8,
      source: "pointer"
    };
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    event.preventDefault();

    this.pointers.set(event.pointerId, {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY
    });
    this.activePointerId = event.pointerId;

    if (event.target instanceof HTMLElement) {
      event.target.setPointerCapture?.(event.pointerId);
    }

    this.update();
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (!this.pointers.has(event.pointerId)) {
      return;
    }

    event.preventDefault();

    this.pointers.set(event.pointerId, {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY
    });

    this.update();
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    event.preventDefault();

    this.pointers.delete(event.pointerId);

    if (event.target instanceof HTMLElement && event.target.hasPointerCapture?.(event.pointerId)) {
      event.target.releasePointerCapture?.(event.pointerId);
    }

    if (this.activePointerId === event.pointerId) {
      this.activePointerId = this.lastPointerId();
    }

    this.update();
  };

  private readonly onPointerLost = (event: PointerEvent): void => {
    this.pointers.delete(event.pointerId);

    if (this.activePointerId === event.pointerId) {
      this.activePointerId = this.lastPointerId();
    }

    this.update();
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (this.setKey(event.code, true)) {
      event.preventDefault();
      this.update();
    }
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    if (this.setKey(event.code, false)) {
      event.preventDefault();
      this.update();
    }
  };

  private readonly onBlur = (): void => {
    this.keys.up = false;
    this.keys.down = false;
    this.keys.left = false;
    this.keys.right = false;
    this.pointers.clear();
    this.activePointerId = null;
    this.update();
  };

  private activePointer(): PointerState | null {
    if (this.activePointerId === null) {
      return null;
    }

    return this.pointers.get(this.activePointerId) ?? null;
  }

  private lastPointerId(): number | null {
    let lastId: number | null = null;

    for (const id of this.pointers.keys()) {
      lastId = id;
    }

    return lastId;
  }

  private setKey(code: string, value: boolean): boolean {
    switch (code) {
      case "ArrowUp":
      case "KeyW":
        this.keys.up = value;
        return true;
      case "ArrowDown":
      case "KeyS":
        this.keys.down = value;
        return true;
      case "ArrowLeft":
      case "KeyA":
        this.keys.left = value;
        return true;
      case "ArrowRight":
      case "KeyD":
        this.keys.right = value;
        return true;
      default:
        return false;
    }
  }
}

function normalize(x: number, y: number): { x: number; y: number } {
  const length = Math.hypot(x, y);

  if (length <= Number.EPSILON) {
    return { x: 0, y: 0 };
  }

  return {
    x: x / length,
    y: y / length
  };
}